// Shared types + display helpers for the read-only Documents feature.
// Documents are cataloged from Google Drive; the cockpit only links out to them.
import { FileText, FileType, FileSpreadsheet, Image as ImageIcon } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type DocumentRow = {
  id: string
  category: string
  category_label: string | null
  attaches_to: 'vehicle' | 'owner' | string
  title: string | null
  drive_url: string | null
  storage_path: string | null
  mime_type: string | null
  // bigint arrives as a string over the wire.
  size_bytes: string | number | null
  // From v_person_documents (owner dialog). Absent on vehicle dialog rows,
  // which all come from vehicle_documents.
  source_table?: 'vehicle_documents' | 'owner_onboarding_documents' | 'driver_documents' | string
  status?: string | null
  // Set only when the status needs saying: pending, rejected, missing in Drive.
  status_label?: string | null
  vehicle_registration?: string | null
}

// Display labels keyed by category code.
export const CATEGORY_LABELS: Record<string, string> = {
  registration_licensing: 'Registration & licensing',
  service: 'Service invoices & receipts',
  repairs: 'Repairs invoices & receipts',
  insurance: 'Insurance claims',
  traffic_fines: 'Traffic fines & penalties',
  // Missing until 27 September 2026, so incident photographs grouped as "Other".
  incident_evidence: 'Incident evidence',
  // One-car PDFs of the shared insurer and tracking schedules (migration 0315).
  insurance_tracking: 'Insurance & tracking schedules',
  owner_information: 'Owner information',
  onboarding: 'Onboarding documents',
  driver: 'Driver documents',
  other: 'Other',
}

// Canonical display order — vehicle categories first, then owner, then other.
export const CATEGORY_ORDER: string[] = [
  'registration_licensing',
  'service',
  'repairs',
  'insurance',
  'insurance_tracking',
  'traffic_fines',
  'incident_evidence',
  'owner_information',
  'onboarding',
  'driver',
  'other',
]

const CATEGORY_RANK: Record<string, number> = Object.fromEntries(
  CATEGORY_ORDER.map((code, i) => [code, i]),
)

export function categoryLabel(code: string | null | undefined): string {
  if (!code) return 'Other'
  return CATEGORY_LABELS[code] ?? 'Other'
}

// A group of documents sharing a category, ready to render in display order.
export type DocumentGroup = {
  code: string
  label: string
  items: DocumentRow[]
}

// Groups rows by category and returns groups in canonical display order.
// Unknown categories sort after known ones, alphabetically.
export function groupByCategory(rows: DocumentRow[]): DocumentGroup[] {
  const byCode = new Map<string, DocumentRow[]>()
  for (const row of rows) {
    const code = row.category || 'other'
    const list = byCode.get(code)
    if (list) list.push(row)
    else byCode.set(code, [row])
  }
  return [...byCode.entries()]
    .sort(([a], [b]) => {
      const ra = CATEGORY_RANK[a] ?? Number.MAX_SAFE_INTEGER
      const rb = CATEGORY_RANK[b] ?? Number.MAX_SAFE_INTEGER
      if (ra !== rb) return ra - rb
      return a.localeCompare(b)
    })
    .map(([code, items]) => ({ code, label: categoryLabel(code), items }))
}

// Returns a lucide icon component appropriate for the given mime type.
export function fileIcon(mime: string | null | undefined): LucideIcon {
  const m = (mime ?? '').toLowerCase()
  if (m === 'application/pdf') return FileText
  if (m.startsWith('image/')) return ImageIcon
  if (
    m.includes('wordprocessingml') ||
    m === 'application/msword' ||
    m.includes('opendocument.text')
  ) {
    return FileType
  }
  if (
    m.includes('spreadsheetml') ||
    m === 'application/vnd.ms-excel' ||
    m.includes('opendocument.spreadsheet') ||
    m.includes('csv')
  ) {
    return FileSpreadsheet
  }
  return FileText
}

// Human-readable file size, or null when the size is unknown / zero.
export function formatFileSize(bytes: string | number | null | undefined): string | null {
  const n = typeof bytes === 'string' ? Number(bytes) : bytes
  if (n == null || !Number.isFinite(n) || n <= 0) return null
  const units = ['B', 'KB', 'MB', 'GB']
  let value = n
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  const rounded = value >= 100 || unit === 0 ? Math.round(value) : Math.round(value * 10) / 10
  return `${rounded} ${units[unit]}`
}

// Compact relative time ("just now", "5 min ago", "3 days ago"), for the
// "last synced" footer. Falls back to a locale date for older timestamps.
export function relativeTime(value: string | null | undefined): string {
  if (!value) return 'never'
  const then = new Date(value)
  if (Number.isNaN(then.getTime())) return 'never'
  const diffMs = Date.now() - then.getTime()
  const diffSec = Math.round(diffMs / 1000)
  if (diffSec < 45) return 'just now'
  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return `${diffMin} min ago`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`
  const diffDay = Math.round(diffHr / 24)
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`
  return then.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
}
